import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, Award, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { cacheGet, cacheSet } from "../../lib/cache";
import BackButton from "../../components/BackButton";
import { ProfileAvatar } from "../../components/ProfilePhoto";
import { Spinner, formatRupiah } from "../../components/ui";
import { StarRating } from "../../components/StarRating";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function serviceCoverSrc(svc) {
  if (!svc?.cover_photo_url) return null;
  if (/^https?:\/\//.test(svc.cover_photo_url)) return svc.cover_photo_url;
  return `${BACKEND_URL}${svc.cover_photo_url}`;
}

export default function CustomerWorkerProfile() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const serviceSlug = searchParams.get("service") || "";
  const [worker, setWorker] = useState(null);
  const [services, setServices] = useState(() => cacheGet(`worker-svc:${id}`).data || []);
  const [loading, setLoading] = useState(true);
  const [reviewsData, setReviewsData] = useState(() => cacheGet(`worker-reviews:${id}`).data);

  useEffect(() => {
    const fetchWorker = async () => {
      try {
        setLoading(true);
        if (serviceSlug) {
          const { data } = await api.get(`/api/services/${serviceSlug}`);
          const w = data.workers?.find((wr) => wr.worker_id === id);
          if (w) {
            setWorker(w);
          }
        }
        // Fallback: try to get worker's services list
        const { data: svcData } = await api.get(`/api/workers/${id}/services`);
        setServices(Array.isArray(svcData) ? svcData : []);
        cacheSet(`worker-svc:${id}`, Array.isArray(svcData) ? svcData : [], 300000);

        try {
          const { data } = await api.get(`/api/reviews/worker/${id}`);
          setReviewsData(data);
          cacheSet(`worker-reviews:${id}`, data, 300000);
        } catch (e) { /* ignore */ }
      } catch (err) {
        console.error("Failed to load worker:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchWorker();
  }, [id, serviceSlug]);

  if (loading) return <Spinner />;
  if (!worker) {
    return (
      <div className="animate-fade-up grid place-items-center py-8">
        <div className="container space-y-6">
          <BackButton to="/customer/services" />
          <div className="card p-10 text-center">
            <p className="font-bold text-brand-800">Pekerja tidak ditemukan</p>
            <Link to="/customer/services" className="btn-primary mt-4 inline-flex">
              Kembali ke layanan
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const initials = (worker.display_name || "P")[0].toUpperCase();
  void initials;

  return (
    <div className="animate-fade-up grid place-items-center py-8">
      <div className="container space-y-6">
        <BackButton to={serviceSlug ? `/customer/services/${serviceSlug}` : "/customer/services"} />

        {/* Worker Header */}
        <div className="card p-6">
          <div className="flex items-center gap-4">
            <ProfileAvatar userId={id} photoUrl={worker.profile_photo_url} name={worker.display_name} />
            <div>
              <h1 className="text-brand-800 text-xl">{worker.display_name || "Pekerja"}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="chip bg-brand-50 text-brand-600 text-xs flex items-center gap-1">
                  <Award className="h-3 w-3" /> {worker.tier}
                </span>
                {worker.kyc_status === "APPROVED" ? (
                <span className="flex items-center gap-1 text-sm text-success-600">
                  <ShieldCheck className="h-4 w-4" /> Terverifikasi
                </span>
              ) : (
                <span className="text-sm text-brand-400">Belum terverifikasi</span>
              )}
              {reviewsData && reviewsData.average_rating > 0 && (
                <StarRating rating={reviewsData.average_rating} count={reviewsData.total_reviews} size="lg" />
              )}
              </div>
            </div>
          </div>
        </div>

        {/* Worker Services */}
        <div>
          <h2 className="text-brand-800">Layanan yang Ditawarkan</h2>
          {services.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              {services.map((svc) => (
                <div key={svc.id} className="card p-4 hover:border-brand-200 transition flex flex-col">
                  {serviceCoverSrc(svc) && (
                    <img
                      src={serviceCoverSrc(svc)}
                      alt={svc.service?.name || "Foto hasil kerja"}
                      className="h-40 w-full rounded-xl object-cover border border-brand-100 mb-3"
                      loading="lazy"
                    />
                  )}
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div>
                      <p className="font-bold text-brand-800">{svc.service?.name || "Layanan"}</p>
                      {svc.description_override && (
                        <p className="text-sm text-brand-500 mt-1">{svc.description_override}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-extrabold text-brand-800">
                        {formatRupiah(svc.custom_price || svc.service?.base_price)}
                      </p>
                      <p className="text-xs text-brand-400">
                        {svc.custom_price ? "Harga kustom" : "Harga dasar"}
                      </p>
                    </div>
                  </div>
                  <div className="mt-auto pt-3 border-t border-brand-50">
                    <Link
                      to={`/customer/bookings/create/${id}?worker_service_id=${svc.id}&service_name=${encodeURIComponent(svc.service?.name || "")}&price=${svc.custom_price || svc.service?.base_price || ""}`}
                      className="btn-primary w-full text-sm !py-2.5"
                      data-testid={`book-service-${svc.id}`}
                    >
                      Pesan Sekarang
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card p-8 text-center mt-4">
              <p className="font-bold text-brand-800">Tidak ada layanan aktif</p>
              <p className="text-brand-400 mt-1">Pekerja ini belum menawarkan layanan.</p>
            </div>
          )}
        </div>

        {/* Reviews */}
        {reviewsData && reviewsData.reviews.length > 0 && (
          <div>
            <h2 className="text-brand-800">Review dari Pelanggan ({reviewsData.total_reviews})</h2>
            <div className="space-y-3 mt-4">
              {reviewsData.reviews.map((r) => (
                <div key={r.id} className="card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-bold text-brand-800 text-sm">{r.reviewer_name || "Pelanggan"}</p>
                    <StarRating rating={r.rating} />
                  </div>
                  {r.comment && <p className="text-sm text-brand-600">{r.comment}</p>}
                  <p className="text-xs text-brand-400 mt-2">{new Date(r.created_at).toLocaleDateString("id-ID")}</p>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
